import i18next from 'i18next';
import { toast } from 'sonner';
import { useSystemLogsStore } from '../../store/system-logs.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { normalizeSystemLogDateFilters } from '../system-log-export.js';
import { LatestRequestGate } from '../system-log-explorer.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

const currentTenantKey = () => String(useTenantStore.getState().selectedTenantId ?? '');
const isCurrentTenant = (tenantKey) => currentTenantKey() === tenantKey;

export class SystemLogsIntegration {
    constructor(sdkService, accessLogsService = null) {
        this.service = sdkService;
        this.accessLogsService = accessLogsService;
        this.listRequestGate = new LatestRequestGate();
    }

    async fetchAll(params = {}) {
        const store = useSystemLogsStore.getState();
        const tenantKey = currentTenantKey();
        const requestToken = this.listRequestGate.begin(tenantKey);
        const canWrite = () => isCurrentTenant(tenantKey)
            && this.listRequestGate.isLatest(requestToken, currentTenantKey());
        store.setLoading(true);
        store.clearError();

        try {
            const response = await this.service.getAll(normalizeSystemLogDateFilters(params));
            if (!canWrite()) return { success: false, stale: true };
            store.setItems(response.data);
            if (response.pagination) store.setPaginationData(response.pagination);
            if (response.uniqueValues) store.setUniqueValues(response.uniqueValues);
            return { success: true, data: response.data };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            if (!canWrite()) return { success: false, stale: true };
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (canWrite()) store.setLoading(false);
        }
    }

    async fetchEntityActivity(entityType, entityId, params = {}) {
        const tenantKey = currentTenantKey();
        try {
            const response = await this.service.getEntityActivity(entityType, entityId, params);
            if (!isCurrentTenant(tenantKey)) return { success: false, stale: true, data: [], pagination: {} };
            const data = response.data || [];
            const pagination = response.pagination || {};
            return { success: true, data, pagination };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            if (isCurrentTenant(tenantKey)) toast.error(message);
            return { success: false, error: message, data: [], pagination: {} };
        }
    }

    async fetchById(id, { openDrawer = false } = {}) {
        const store = useSystemLogsStore.getState();
        const tenantKey = currentTenantKey();
        store.setDetailLoading(true);
        store.setRelatedLogs([]);

        try {
            const response = await this.service.getById(id);
            if (!isCurrentTenant(tenantKey)) return { success: false, stale: true };
            store.setSelectedLog(response.data);
            if (response.data?.relatedLogs) store.setRelatedLogs(response.data.relatedLogs);
            if (openDrawer && response.data) store.openDetail(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            if (isCurrentTenant(tenantKey)) toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        } finally {
            if (isCurrentTenant(tenantKey)) store.setDetailLoading(false);
        }
    }

    async fetchStatSummary(params = {}, { tenantId } = {}) {
        const store = useSystemLogsStore.getState();
        const expectedTenantId = String(tenantId ?? useTenantStore.getState().selectedTenantId ?? '');
        store.setStatBar({ loading: true });
        try {
            const response = await this.service.getStatSummary(params);
            if (String(useTenantStore.getState().selectedTenantId ?? '') !== expectedTenantId) {
                return { success: false, stale: true };
            }
            const d = response.data || {};
            store.setStatBar({
                loading: false,
                totalToday: d.totalToday ?? 0,
                errorCount: d.errorCount ?? 0,
                activeUsers: d.activeUsers ?? 0,
                criticalActions: d.criticalActions ?? 0
            });
            return { success: true, data: d };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            if (String(useTenantStore.getState().selectedTenantId ?? '') === expectedTenantId) {
                store.setStatBar({ loading: false });
                toast.error(message);
            }
            return { success: false, error: message };
        }
    }

    async fetchStats(params = {}) {
        const store = useSystemLogsStore.getState();
        const tenantId = String(useTenantStore.getState().selectedTenantId ?? '');
        store.setStatsLoading(true);

        try {
            const response = await this.service.getStats(params);
            if (String(useTenantStore.getState().selectedTenantId ?? '') !== tenantId) return { success: false, stale: true };
            store.setStats(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            if (String(useTenantStore.getState().selectedTenantId ?? '') === tenantId) toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        } finally {
            if (String(useTenantStore.getState().selectedTenantId ?? '') === tenantId) store.setStatsLoading(false);
        }
    }

    async fetchHeatmap(params = {}) {
        const store = useSystemLogsStore.getState();
        const tenantId = String(useTenantStore.getState().selectedTenantId ?? '');
        store.setHeatmapLoading(true);

        try {
            const response = await this.service.getHeatmap(params);
            if (String(useTenantStore.getState().selectedTenantId ?? '') !== tenantId) return { success: false, stale: true };
            store.setHeatmapData(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            if (String(useTenantStore.getState().selectedTenantId ?? '') === tenantId) toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        } finally {
            if (String(useTenantStore.getState().selectedTenantId ?? '') === tenantId) store.setHeatmapLoading(false);
        }
    }

    async fetchSecuritySummary() {
        const store = useSystemLogsStore.getState();
        const tenantId = String(useTenantStore.getState().selectedTenantId ?? '');
        store.setSecurityLoading(true);

        try {
            const response = await this.service.getSecuritySummary();
            if (String(useTenantStore.getState().selectedTenantId ?? '') !== tenantId) return { success: false, stale: true };
            store.setSecuritySummary(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            if (String(useTenantStore.getState().selectedTenantId ?? '') === tenantId) toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        } finally {
            if (String(useTenantStore.getState().selectedTenantId ?? '') === tenantId) store.setSecurityLoading(false);
        }
    }

    async fetchDeltaPicker(params = {}) {
        const store = useSystemLogsStore.getState();
        const tenantKey = currentTenantKey();
        store.setDeltaPickerLoading(true);
        try {
            const response = await this.service.getDeltaPicker(params);
            if (!isCurrentTenant(tenantKey)) return { success: false, stale: true };
            store.setDeltaPickerItems(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            if (isCurrentTenant(tenantKey)) toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        } finally {
            if (isCurrentTenant(tenantKey)) store.setDeltaPickerLoading(false);
        }
    }

    async fetchTimeline(resourceType, resourceId, params = {}, options = {}) {
        const { append = false } = options;
        const store = useSystemLogsStore.getState();
        const tenantKey = currentTenantKey();
        if (append) store.setTimelineAppending(true);
        else store.setTimelineLoading(true);

        try {
            const response = await this.service.getTimeline(resourceType, resourceId, params);
            if (!isCurrentTenant(tenantKey)) return { success: false, stale: true };
            if (append) {
                const prev = useSystemLogsStore.getState().entityTimeline || [];
                store.setEntityTimeline([...prev, ...(response.data || [])]);
            } else {
                store.setEntityTimeline(response.data || []);
            }
            if (response.pagination) store.setTimelinePagination(response.pagination);
            return { success: true, data: response.data };
        } catch (error) {
            if (isCurrentTenant(tenantKey)) toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        } finally {
            if (isCurrentTenant(tenantKey)) {
                if (append) store.setTimelineAppending(false);
                else store.setTimelineLoading(false);
            }
        }
    }

    async fetchUserActivity(userId, params = {}) {
        const store = useSystemLogsStore.getState();
        const tenantKey = currentTenantKey();
        store.setLoading(true);

        try {
            const response = await this.service.getUserActivity(userId, params);
            if (!isCurrentTenant(tenantKey)) return { success: false, stale: true };
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            if (isCurrentTenant(tenantKey)) toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        } finally {
            if (isCurrentTenant(tenantKey)) store.setLoading(false);
        }
    }

    async estimateExport(filters = {}) {
        const tenantKey = currentTenantKey();
        try {
            const response = await this.service.estimateExport(normalizeSystemLogDateFilters(filters));
            if (!isCurrentTenant(tenantKey)) return { success: false, stale: true };
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }

    async exportLogs(filters = {}, format = 'json') {
        const store = useSystemLogsStore.getState();
        const tenantKey = currentTenantKey();
        store.setExportLoading(true);

        try {
            const response = await this.service.exportLogs(normalizeSystemLogDateFilters(filters), format);
            if (!isCurrentTenant(tenantKey)) return { success: false, stale: true };
            toast.success(i18next.t('integrationToasts:logsExportedSuccessfully', { defaultValue: 'Logs exported successfully' }));
            return {
                success: true,
                data: response.data,
                total: response.total,
                metadata: response.metadata,
            };
        } catch (error) {
            if (isCurrentTenant(tenantKey)) toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        } finally {
            if (isCurrentTenant(tenantKey)) store.setExportLoading(false);
        }
    }

    async fetchRetentionInfo({ tenantId } = {}) {
        const store = useSystemLogsStore.getState();
        const expectedTenantId = tenantId == null ? null : String(tenantId);

        try {
            const response = await this.service.getRetentionInfo();
            const currentTenantId = useTenantStore.getState().selectedTenantId;
            if (expectedTenantId !== null && String(currentTenantId) !== expectedTenantId) {
                return { success: false, stale: true };
            }
            store.setRetentionInfo(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }

    async fetchLiveEntries(params = {}) {
        const store = useSystemLogsStore.getState();
        const tenantKey = currentTenantKey();
        store.setLiveLoading(true);

        try {
            const { liveActionFamilies } = store;
            const merged = { ...params, pageSize: 50, sortBy: 'createdAt', sortOrder: 'desc' };
            if (liveActionFamilies) merged.actionFamilies = liveActionFamilies;
            const response = await this.service.getAll(merged);
            if (!isCurrentTenant(tenantKey)) return { success: false, stale: true };
            store.setLiveEntries(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: getIntegrationErrorMessage(error) };
        } finally {
            if (isCurrentTenant(tenantKey)) store.setLiveLoading(false);
        }
    }

    async pollNewEntries(lastTimestamp) {
        const store = useSystemLogsStore.getState();
        const tenantKey = currentTenantKey();

        try {
            const t = new Date(lastTimestamp).getTime();
            const afterIso = Number.isNaN(t) ? lastTimestamp : new Date(t + 1).toISOString();

            const { liveActionFamilies } = store;
            const merged = {
                dateFrom: afterIso,
                pageSize: 50,
                sortBy: 'createdAt',
                sortOrder: 'desc'
            };
            if (liveActionFamilies) {
                merged.actionFamilies = liveActionFamilies;
            }
            const response = await this.service.getAll(merged);
            if (!isCurrentTenant(tenantKey)) return { success: false, stale: true };
            if (response.data?.length > 0) store.prependLiveEntries(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }

    async fetchAccessLogs(params = {}) {
        if (!this.accessLogsService) return { success: false, error: 'Access logs servisi kullanılamıyor' };
        const store = useSystemLogsStore.getState();
        const tenantKey = currentTenantKey();
        store.setAccessLogsLoading(true);
        try {
            const merged = { ...store.accessLogParams, ...params };
            store.setAccessLogParams(merged);
            const response = await this.accessLogsService.getAll(merged);
            if (!isCurrentTenant(tenantKey)) return { success: false, stale: true };
            store.setAccessLogItems(response.data);
            if (response.pagination) store.setAccessLogPagination(response.pagination);
            return { success: true, data: response.data };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            if (isCurrentTenant(tenantKey)) toast.error(message);
            return { success: false, error: message };
        } finally {
            if (isCurrentTenant(tenantKey)) store.setAccessLogsLoading(false);
        }
    }

    /** Integrity card — no toast; caller maps empty/error to unavailable. */
    async verifyIntegrity(params = {}) {
        return this.service.verifyIntegrity(params);
    }

    /** Ad-hoc query without writing the explorer store. */
    async queryAll(params = {}) {
        return this.service.getAll(params);
    }

}
